// Описание поездки для превью ссылки: строка, которую мессенджер или соцсеть
// покажут под заголовком вместо голого адреса.
//
// Считается здесь, в браузере автора, а не на сервере, и уезжает вместе с
// публикацией. Причин две. Расчёт стоимости живёт в src/core и на сервере его
// нет: дублировать конвертацию валют, кратность на человека и RT-парность —
// значит завести второй источник истины по деньгам. И язык: словарь тоже
// живёт здесь, а автор поездки уже выбрал, на каком языке работает, — испанец
// делится испанским описанием, и его друзьям приходит превью по-испански.
import type { AppState, VariantTotals } from '../types';
import { computeVariantTotals, formatMoney } from '../core';
import type { Translate } from '../i18n';

/**
 * Потолок длины. Facebook и Telegram обрезают описание в районе 200 знаков,
 * поисковая выдача — раньше; обрезать самим лучше, чем отдать хвост, который
 * оборвётся посреди суммы.
 */
export const MAX_PREVIEW_LEN = 200;

/** Обрезка по границе слова, с многоточием — иначе описание рвётся посреди слова. */
function clamp(text: string, limit = MAX_PREVIEW_LEN): string {
  if (text.length <= limit) return text;
  const cut = text.slice(0, limit - 1);
  const space = cut.lastIndexOf(' ');
  return `${(space > limit * 0.6 ? cut.slice(0, space) : cut).trimEnd()}…`;
}

/**
 * Маршрут варианта одной строкой: 'LCA → EVN → MSQ'. Пустая строка, если
 * перелётов в варианте нет, — тогда в описании его просто не будет.
 *
 * Повтор кода подряд («прилетели в EVN, оттуда же вылетели») схлопывается:
 * в цепочке это одна точка, а не две.
 */
function routeOf(state: AppState, variantIndex: number): string {
  const variant = state.variants[variantIndex];
  if (!variant) return '';
  const airports: string[] = [];
  for (const id of variant.segmentIds) {
    const segment = state.library[id];
    if (segment?.kind !== 'flight') continue;
    for (const code of [segment.from, segment.to]) {
      if (code && airports[airports.length - 1] !== code) airports.push(code);
    }
  }
  return airports.join(' → ');
}

/**
 * Самый дешёвый вариант — тот, вокруг которого строится и превью ссылки, и сама
 * публичная страница. Правило одно на двоих намеренно: разойдись они, карточка
 * в мессенджере обещала бы одну цену, а страница показывала другую — и первое,
 * что читатель узнаёт о сервисе, было бы «он врёт».
 *
 * Пустой список — это −1: «дешёвого варианта нет» надо уметь отличить от
 * «дешёвый вариант первый».
 */
export function cheapestIndex(totals: readonly VariantTotals[]): number {
  if (totals.length === 0) return -1;
  let cheapest = 0;
  for (let i = 1; i < totals.length; i += 1) {
    if (totals[i].totalWithBaggageMinor < totals[cheapest].totalWithBaggageMinor) cheapest = i;
  }
  return cheapest;
}

/**
 * Описание поездки: маршрут, число вариантов, цена самого дешёвого и ночи.
 *
 * Опорой берётся самый дешёвый вариант — по ссылке приходят именно за ответом
 * «во сколько обойдётся», и маршрут показывать логично тот же, к которому
 * относится цена.
 */
export function tripPreview(state: AppState, t: Translate): string {
  if (state.variants.length === 0) return t('share.preview.empty');

  const totals = state.variants.map((variant) => computeVariantTotals(variant, state, t));
  const cheapest = cheapestIndex(totals);
  const best = totals[cheapest];
  const parts: string[] = [];

  const route = routeOf(state, cheapest);
  if (route) parts.push(route);

  parts.push(t('share.preview.variants', { count: state.variants.length }));

  // Нулевая сумма — это не «бесплатно», а «цены ещё не проставлены»: такую
  // строку в описание лучше не выносить вовсе.
  if (best.totalWithBaggageMinor > 0) {
    const price = formatMoney(best.totalWithBaggageMinor, best.currency, state.currencies);
    parts.push(t('share.preview.from', { price }));
  }

  if (best.nights > 0) parts.push(t('summary.nights', { count: best.nights }));

  return clamp(`${parts.join(' · ')}. ${t('share.preview.tail')}`);
}
